import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar
} from '@mui/material';
import {
  CheckCircle,
  Diamond,
  Phone,
  Payment,
  TrendingUp,
  Storage,
  Download,
  Support,
  Security,
  Check,
  Timer,
  Bolt,
  ContentCopy,
  CardGiftcard,
  Group
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { LARGE_PDF_UPLOAD_NOTICE } from './uploadHelpText';
import MobileAppCta from './MobileAppCta';
import axios from 'axios';
import tracker from './activityTracker';

const Subscription = ({ onTabChange }) => {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [dayPasses, setDayPasses] = useState([]);
  const [activeDayPass, setActiveDayPass] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [referralSummary, setReferralSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isDayPassPurchase, setIsDayPassPurchase] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchSubscriptionData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      
      // Fetch public products independently so one failed request cannot hide the other.
      const [plansResult, dayPassesResult] = await Promise.allSettled([
        axios.get(`${API_URL}/subscription/plans`),
        axios.get(`${API_URL}/daily-pass/products`),
      ]);

      if (plansResult.status === 'fulfilled') {
        const publicPlans = plansResult.value.data?.plans || [];
        setPlans(publicPlans.filter(p => !p.id.startsWith('day_pass_')));
      } else {
        console.error('Error fetching public plans:', plansResult.reason);
        setPlans([]);
        showSnackbar('Failed to load 30-day plans. Please refresh and try again.', 'error');
      }

      if (dayPassesResult.status === 'fulfilled') {
        setDayPasses(dayPassesResult.value.data?.products || []);
      } else {
        console.error('Error fetching public Day Passes:', dayPassesResult.reason);
        setDayPasses([]);
        if (plansResult.status === 'fulfilled') {
          showSnackbar('Plans loaded, but Day Passes are temporarily unavailable.', 'warning');
        }
      }
      
      // Only fetch subscription status and active day pass if authenticated
      if (isAuthenticated) {
        try {
          const authHeaders = await getAuthHeaders();
          const [statusResponse, activeDayPassResponse, referralSummaryResponse] = await Promise.all([
            axios.get(`${API_URL}/subscription/status`, { headers: authHeaders }),
            axios.get(`${API_URL}/daily-pass/active`, { headers: authHeaders }),
            axios.get(`${API_URL}/referrals/summary`, { headers: authHeaders }),
          ]);
          setSubscriptionStatus(statusResponse.data);
          setActiveDayPass(activeDayPassResponse.data.pass);
          setReferralSummary(referralSummaryResponse.data.summary);
        } catch (statusError) {
          console.error('Error fetching subscription status:', statusError);
          if (isAuthenticated) {
            showSnackbar('Failed to load subscription status', 'error');
          }
        }
      } else {
        // Set default status for unauthenticated users
        setSubscriptionStatus(null);
        setActiveDayPass(null);
        setReferralSummary(null);
      }
      
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      showSnackbar('Failed to load subscription plans', 'error');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [getAuthHeaders, showSnackbar, isAuthenticated]);

  useEffect(() => {
    // Fetch plans even if user is not authenticated
    if (!hasFetchedRef.current && !isFetchingRef.current) {
      fetchSubscriptionData();
    }
  }, [fetchSubscriptionData]);

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  const handleSubscribe = (plan, isPass = false) => {
    if (!isAuthenticated) {
      showSnackbar('Please log in to buy a plan', 'warning');
      if (onTabChange) {
        onTabChange(1);
      }
      return;
    }
    tracker.trackFeatureUse(isPass ? 'purchase_day_pass' : 'subscribe_plan', 'subscription', { plan_name: plan?.name });
    setSelectedPlan(plan);
    setIsDayPassPurchase(isPass);
    setPaymentDialogOpen(true);
  };

  const handlePayment = async () => {
    if (!phoneNumber.trim()) {
      showSnackbar('Please enter your phone number', 'error');
      return;
    }

    try {
      setPaymentLoading(true);
      const formData = new FormData();
      formData.append('phone_number', phoneNumber);

      // Get auth headers first (it's async)
      const authHeaders = await getAuthHeaders();
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

      // Choose endpoint based on whether it's a day pass or subscription
      let endpoint;
      if (isDayPassPurchase) {
        formData.append('pass_type', selectedPlan.id);
        endpoint = `${API_URL}/daily-pass/purchase`;
      } else {
        formData.append('plan_id', selectedPlan.id);
        endpoint = `${API_URL}/subscription/payment/initiate`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setPaymentDialogOpen(false);
        setPhoneNumber('');
        setIsDayPassPurchase(false);
        // Refresh subscription status - reset the flag first to allow refetch
        hasFetchedRef.current = false;
        fetchSubscriptionData();
      } else {
        showSnackbar('Payment initiation failed', 'error');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showSnackbar(error.response?.data?.detail || 'Payment failed', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free':
        return 'success';
      case 'starter':
        return 'primary';
      case 'professional':
        return 'secondary';
      case 'business':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isCurrentPlan = (planId) => {
    return subscriptionStatus?.status?.plan_id === planId && subscriptionStatus?.status?.has_active_subscription;
  };

  const getFeatureIcon = (feature) => {
    const f = feature.toLowerCase();
    if (f.includes('page') || f.includes('upload')) return <Storage color="primary" />;
    if (f.includes('analytics')) return <TrendingUp color="primary" />;
    if (f.includes('export')) return <Download />;
    if (f.includes('support')) return <Support />;
    if (f.includes('api')) return <Security />;
    if (f.includes('white-label')) return <Diamond />;
    return <Check />;
  };

  const getPageLimitLabel = (plan) => {
    if (plan?.id === 'free' && plan?.max_pages_24h && plan?.max_pages_month) {
      return `${plan.max_pages_24h} pages / 24h · ${plan.max_pages_month} pages / 30 days`;
    }
    if (plan?.max_pages_24h) return `${plan.max_pages_24h} pages / 24h`;
    if (plan?.max_pages_month) return `${plan.max_pages_month.toLocaleString()} pages for 30 days`;
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires - now;
    if (diffMs <= 0) return 'Expired';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  const getActivePassTitle = (pass) => {
    if (!pass?.pass_type) return 'Active Day Pass';
    if (pass.pass_type.startsWith('referral_')) return 'Active Referral Bonus';
    return 'Active Day Pass';
  };

  const copyReferralLink = async () => {
    if (!referralSummary?.referral_code) return;
    const link = `${window.location.origin}/?ref=${referralSummary.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      showSnackbar('Referral link copied to clipboard', 'success');
    } catch (error) {
      showSnackbar('Failed to copy referral link', 'error');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', mt: 2 }}>
          {LARGE_PDF_UPLOAD_NOTICE}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', mt: 1 }}>
          <Box component="a" href="/split-pdf" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Split PDF tool
          </Box>
          {' '}— download a ZIP of smaller files (default 120 pages each), then upload each part.
        </Typography>
      </Box>

      <MobileAppCta compact />

      {/* Login Prompt for Unauthenticated Users */}
      {!isAuthenticated && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body1">
            You can browse all plans without logging in. Log in only when you are ready to buy a 30-day plan or Day Pass.
          </Typography>
        </Alert>
      )}

      {/* Active Day Pass Banner */}
      {activeDayPass && (
        <Paper sx={{ p: 3, mb: 4, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Bolt sx={{ mr: 1, fontSize: 28 }} />
              <Box>
                <Typography variant="h6">
                  {getActivePassTitle(activeDayPass)}: {activeDayPass.pages_allowed} pages
                </Typography>
                <Typography variant="body2">
                  {activeDayPass.pages_remaining || (activeDayPass.pages_allowed - (activeDayPass.pages_used || 0))} pages remaining • {formatTimeRemaining(activeDayPass.expires_at)}
                </Typography>
              </Box>
            </Box>
            <Chip
              label="Active"
              color="info"
              icon={<Timer />}
            />
          </Box>
        </Paper>
      )}

      {/* Current Subscription Status */}
      {subscriptionStatus?.status?.has_active_subscription && (
        <Paper sx={{ p: 3, mb: 4, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">
                Current Plan: {subscriptionStatus.status.plan_name}
              </Typography>
              <Typography variant="body2">
                {subscriptionStatus.status.days_remaining} days remaining
              </Typography>
            </Box>
            <Chip
              label="Active"
              color="success"
              icon={<CheckCircle />}
            />
          </Box>
        </Paper>
      )}

      {isAuthenticated && referralSummary && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardGiftcard color="primary" />
                Referral Program
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Share your referral link. You earn 100 pages when a referred user activates their account, then 500 more pages when they buy their first paid 30-day plan.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Referred users receive 500 bonus pages after their first paid 30-day plan.
              </Typography>
            </Box>

            <Button variant="outlined" startIcon={<ContentCopy />} onClick={copyReferralLink}>
              Copy Link
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Your referral code"
                value={referralSummary.referral_code || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Your referral link"
                value={referralSummary.referral_code ? `${window.location.origin}/?ref=${referralSummary.referral_code}` : ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Signup referrals</Typography>
                  <Typography variant="h5">{referralSummary.stats?.signup_referrals || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Paid conversions</Typography>
                  <Typography variant="h5">{referralSummary.stats?.paid_referrals || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Pages earned</Typography>
                  <Typography variant="h5">{referralSummary.stats?.total_pages_earned || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">My referred bonus</Typography>
                  <Typography variant="h5">{referralSummary.stats?.referred_bonus_pages_received || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {referralSummary.referrals?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group fontSize="small" />
                Referral activity
              </Typography>
              <List dense>
                {referralSummary.referrals.slice(0, 5).map((referral) => (
                  <ListItem key={referral.referred_user_id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle color={referral.status === 'paid_rewarded' ? 'success' : 'primary'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={referral.referred_email_masked || referral.referred_user_id}
                      secondary={
                        referral.status === 'paid_rewarded'
                          ? `Converted to paid. Earned ${referral.signup_reward_pages + referral.paid_reward_pages} pages total.`
                          : `Activated account. Earned ${referral.signup_reward_pages} pages so far.`
                      }
                    />
                    <Chip
                      size="small"
                      color={referral.status === 'paid_rewarded' ? 'success' : 'primary'}
                      label={referral.status === 'paid_rewarded' ? 'Paid conversion' : 'Signup reward'}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}

      {/* Day Passes Section */}
      {dayPasses.length > 0 && (
        <>
          <Box textAlign="center" mt={6} mb={4}>
            <Typography variant="h4" component="h2" gutterBottom>
              <Bolt sx={{ verticalAlign: 'middle', mr: 1 }} />
              Day Passes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Need more pages just for today? Get instant access with a one-time Day Pass.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center" sx={{ mb: 6 }}>
            {dayPasses.map((pass) => (
              <Grid item xs={12} sm={6} md={4} key={pass.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 2,
                    borderColor: 'info.main',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    '&:hover': {
                      boxShadow: 8,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s ease-in-out'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pt: 3, textAlign: 'center' }}>
                    <Bolt sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                    <Typography variant="h5" component="h3" gutterBottom>
                      {pass.name}
                    </Typography>
                    
                    <Chip
                      size="medium"
                      icon={<Timer />}
                      label={`${pass.max_pages_24h} pages / 24h`}
                      color="info"
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" color="text.secondary" paragraph>
                      {pass.description}
                    </Typography>

                    <Typography variant="h3" component="div" color="info.main" sx={{ fontWeight: 'bold' }}>
                      {formatPrice(pass.price_kes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      one-time
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="info"
                      size="large"
                      onClick={() => handleSubscribe(pass, true)}
                      startIcon={<Bolt />}
                    >
                      Buy Day Pass
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* One-time 30-day plans */}
      <Box textAlign="center" mt={4} mb={4}>
        <Typography variant="h4" component="h2" gutterBottom>
          30-Day Page Plans
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Buy a fixed number of pages valid for 30 days. Plans are one-time purchases and do not auto-renew.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={6} lg={3} key={plan.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: isCurrentPlan(plan.id) ? 2 : 1,
                borderColor: isCurrentPlan(plan.id) ? `${getPlanColor(plan.id)}.main` : 'divider',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}
            >
              {isCurrentPlan(plan.id) && (
                <Chip
                  label="Current Plan"
                  color={getPlanColor(plan.id)}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="h5" component="h2">
                    {plan.name}
                  </Typography>
                </Box>

                {getPageLimitLabel(plan) && (
                  <Box mb={1.5}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<Storage />}
                      label={getPageLimitLabel(plan)}
                      color={getPlanColor(plan.id)}
                    />
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" paragraph>
                  {plan.description}
                </Typography>

                <Box mb={3}>
                  <Typography variant="h3" component="div" color="primary">
                    {formatPrice(plan.price_kes)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    one-time purchase
                  </Typography>
                </Box>

                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getFeatureIcon(feature)}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              {plan.id !== 'free' && (
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={isCurrentPlan(plan.id) ? "outlined" : "contained"}
                    color={getPlanColor(plan.id)}
                    size="large"
                    onClick={() => handleSubscribe(plan, false)}
                    disabled={isCurrentPlan(plan.id)}
                    startIcon={isCurrentPlan(plan.id) ? <Check /> : <Payment />}
                  >
                    {isCurrentPlan(plan.id) ? 'Current Plan' : 'Buy Plan'}
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {isDayPassPurchase ? <Bolt sx={{ mr: 1 }} /> : <Payment sx={{ mr: 1 }} />}
            {isDayPassPurchase ? `Buy ${selectedPlan?.name}` : `Buy ${selectedPlan?.name} Plan`}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {isDayPassPurchase ? 'Day Pass Details' : 'Plan Details'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {selectedPlan?.description}
            </Typography>
            {isDayPassPurchase && selectedPlan?.max_pages_24h && (
              <Chip
                size="small"
                icon={<Timer />}
                label={`${selectedPlan.max_pages_24h} pages for 24 hours`}
                color="info"
                sx={{ mt: 1 }}
              />
            )}
            <Typography variant="h4" color={isDayPassPurchase ? 'info.main' : 'primary'} sx={{ mt: 1 }}>
              {selectedPlan && formatPrice(selectedPlan.price_kes)}
            </Typography>
            {!isDayPassPurchase && (
              <Typography variant="body2" color="text.secondary">
                One-time payment · pages valid for 30 days · no automatic renewal
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Payment Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your M-Pesa registered phone number to receive a payment request
          </Typography>

          <TextField
            fullWidth
            label="Phone Number"
            placeholder="0712345678 or 254712345678"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Enter your M-Pesa registered phone number"
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              You will receive an M-Pesa STK Push notification on your phone to complete the payment.
              {isDayPassPurchase && ' Your Day Pass will be activated immediately after payment.'}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPaymentDialogOpen(false); setIsDayPassPurchase(false); }}>
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            color={isDayPassPurchase ? 'info' : 'primary'}
            disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={20} /> : <Phone />}
          >
            {paymentLoading ? 'Processing...' : 'Pay with M-Pesa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Subscription;
